import React, { useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, type FieldErrors, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import AlertMsg from '../../../../../components/AlertMsg';
import Loader from '../../../../../components/Loader';
import { useGetAllergens } from '../../../allergens/services';
import { useGetAttributes } from '../../../menu-attributes/services';
import { useGetCategoryList, useGetPrinterList } from '../../services';
import { useGetModifiers } from '../../services/modifiers-service';
import { useGetMenuList, useUploadMenuImage, useCreateMenuItem, useUpdateMenuItem } from '../services';
import {
  BasicInfoSection,
  PricingSection,
  AvailabilitySection,
  DietarySection,
  AdvancedSection,
  MenuItemSummaryCard,
  TranslationCard,
} from '../components';
import ModifiersField from '../components/ModifiersField';
import {
  BTN_COLOR,
  BTN_TEXT_COLOR,
  THEME_COLOR,
  THEME_COLOR3,
  THEME_COLOR4,
} from '../../../../../constants';
import { CURRENCIES } from '../../../../../constants';
import {
  menuItemFormSchema,
  DAYS_OF_WEEK,
} from '../schemas/menu-item-schema';
import type { TMenuItemFormValues } from '../schemas/menu-item-schema';
import type {
  TAddSettingItemProps,
  TAlertState,
  TMenuCategory,
  TCreateMenuItemRequest,
} from '../schemas';
import type { TMenuItemImage, TMenuItemSlim } from '../types/menu-types';
import {
  createDefaultAvailabilitySlots,
  buildSlotsFromMenuItem,
  convertSlotsToRules,
  imageUrlToFileId,
} from '../utils/menu-item-utils';
import { getUniqueColor } from '../../../../../utils/color-generator';

const currencyList = CURRENCIES as Array<{ code: string; symbol: string }>;

const sectionItems = [
  { key: 'basic', label: 'Basic Info', color: getUniqueColor('basic'), bg: getUniqueColor('basic', 10) },
  { key: 'pricing', label: 'Pricing', color: getUniqueColor('pricing'), bg: getUniqueColor('pricing', 10) },
  { key: 'availability', label: 'Availability', color: getUniqueColor('availability'), bg: getUniqueColor('availability', 10) },
  { key: 'dietary', label: 'Dietary & Nutrition', color: getUniqueColor('dietary'), bg: getUniqueColor('dietary', 10) },
  { key: 'advanced', label: 'Advanced', color: getUniqueColor('advanced'), bg: getUniqueColor('advanced', 10) },
];

const AddSettingItem = ({
  restaurantId,
  itemData,
  currencyIcon,
  restaurantDetail,
  onSubmit: onSubmitProp,
  onCancel,
}: TAddSettingItemProps) => {
  const currencyCode = currencyIcon ?? 'GBP';
  const currencySymbol = currencyList.find((c) => c.code === currencyCode)?.symbol ?? currencyCode;

  // ─── Refs for section navigation ─────────────────────────────────────────────
  const basicRef = useRef<HTMLElement>(null);
  const pricingRef = useRef<HTMLElement>(null);
  const availabilityRef = useRef<HTMLElement>(null);
  const dietaryRef = useRef<HTMLElement>(null);
  const advancedRef = useRef<HTMLElement>(null);

  const sectionRefs: Record<string, React.RefObject<HTMLElement>> = {
    basic: basicRef,
    pricing: pricingRef,
    availability: availabilityRef,
    dietary: dietaryRef,
    advanced: advancedRef,
  };

  const scrollToSection = (key: string) => {
    const el = sectionRefs[key]?.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 72;
    window.scrollTo({ behavior: 'smooth', top });
  };

  // ─── Alert state ──────────────────────────────────────────────────────────────
  const [msgAlert, setMsgAlert] = useState<TAlertState>({
    open: false,
    message: '',
    msgType: 'error',
  });
  const [imageList, setImageList] = useState<TMenuItemImage[]>([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sectionsExpanded, setSectionsExpanded] = useState({
    basic: true,
    pricing: true,
    availability: true,
    dietary: true,
    advanced: true,
  });

  const toggleSection = (key: keyof typeof sectionsExpanded) => (next: boolean) =>
    setSectionsExpanded((prev) => ({ ...prev, [key]: next }));

  // ─── Queries ──────────────────────────────────────────────────────────────────
  const { data: allergensList = [] } = useGetAllergens(restaurantId);
  const { data: attributesList = [] } = useGetAttributes(restaurantId);
  const { data: categoryList = [], isLoading: categoriesLoading } =
    useGetCategoryList(restaurantId);
  const { data: printerList = [] } = useGetPrinterList(restaurantId);
  const { data: menuData = [], isLoading: menuLoading } = useGetMenuList(restaurantId);
  const { data: allModifiers = [], isLoading: modifiersLoading } = useGetModifiers(restaurantId);

  // ─── Mutations ────────────────────────────────────────────────────────────────
  const uploadMutation = useUploadMenuImage();
  const createMutation = useCreateMenuItem(restaurantId);
  const updateMutation = useUpdateMenuItem(restaurantId);

  const isLoading = categoriesLoading || menuLoading || modifiersLoading;
  const isSaving = createMutation.isPending || updateMutation.isPending;

  // ─── Hydration guard ──────────────────────────────────────────────────────────
  const hydratedIdRef = useRef<string | null>(null);

  // ─── Form ─────────────────────────────────────────────────────────────────────
  const formMethods = useForm<TMenuItemFormValues>({
    resolver: zodResolver(menuItemFormSchema) as Resolver<TMenuItemFormValues>,
    defaultValues: {
      images: [],
      name: '',
      printName: '',
      description: '',
      _idCategory: '',
      priceValue: undefined as unknown as number,
      priceCurrency: currencyCode,
      takeawayPriceValue: 0,
      enabled: true,
      printerId: null,
      _idAllergens: [],
      attributes: [],
      allergies: '',
      menuType: 'none',
      allowedOrderTypes: [],
      hideMenuThumbnailImages: false,
      calories: '',
      nutritions: '',
      dishnote: '',
      restrictAvailability: false,
      availabilitySlots: createDefaultAvailabilitySlots(),
      _idModifiers: [],
      textColor: null,
      color: null,
    },
  });

  const { handleSubmit, reset, setValue, watch, formState: { isDirty } } = formMethods;

  // ─── Hydrate form when editing ────────────────────────────────────────────────
  useEffect(() => {
    if (!itemData) {
      hydratedIdRef.current = null;
      return;
    }

    if (hydratedIdRef.current === itemData._id) return;

    const imageUrl = itemData.images?.[0]?.image ?? '';

    setImageList(
      imageUrl
        ? [{ id: itemData._id, name: imageUrlToFileId(imageUrl), image: imageUrl, upload: true }]
        : []
    );

    reset({
      images: imageUrl ? [imageUrl] : [],
      name: itemData.name ?? '',
      printName: itemData.printName ?? '',
      description: itemData.description ?? '',
      _idCategory: itemData._idCategory ?? '',
      priceValue: itemData.price?.value ?? 0,
      priceCurrency: currencyCode,
      takeawayPriceValue: itemData.takeawayPrice?.value ?? 0,
      enabled: itemData.enabled ?? true,
      printerId: itemData.printerId ?? null,
      _idAllergens: itemData._idAllergens ?? [],
      attributes: (itemData.attributes ?? []).map((a) => ({
        _idAttribute: a._idAttribute,
        value: a.value ?? '',
        _id: a._id,
      })),
      allergies: itemData.allergies ?? '',
      menuType: itemData.menuType ?? 'none',
      allowedOrderTypes: itemData.allowedOrderTypes ?? [],
      hideMenuThumbnailImages: itemData.hideMenuThumbnailImages ?? false,
      calories: itemData.calories != null ? String(itemData.calories) : '',
      nutritions: itemData.nutritions ?? '',
      dishnote: itemData.dishnote ?? '',
      restrictAvailability: (itemData.availabilityRules?.length ?? 0) > 0,
      availabilitySlots: buildSlotsFromMenuItem(itemData),
      _idModifiers: itemData._idModifiers ?? [],
      textColor: itemData.textColor ?? null,
      color: itemData.color ?? null,
    });

    hydratedIdRef.current = itemData._id;
  }, [itemData, currencyCode, reset]);

  // ─── Image upload ─────────────────────────────────────────────────────────────
  const handleImageUpload = (data: TMenuItemImage, file: File) => {
    setImageList([data]);
    setImageUploading(true);
    uploadMutation.mutate(file, {
      onSuccess: (response) => {
        if (response?.data?.location) {
          setImageList([
            { ...data, name: response.data.fileId, image: response.data.location, upload: true },
          ]);
          setValue('images', [response.data.location], { shouldDirty: true });
        } else {
          setImageList([]);
          setMsgAlert({ open: true, message: 'Image upload failed', msgType: 'error' });
        }
      },
      onError: () => {
        setImageList([]);
        setMsgAlert({ open: true, message: 'Image upload failed', msgType: 'error' });
      },
      onSettled: () => setImageUploading(false),
    });
  };

  const handleRemoveImage = (_data: TMenuItemImage) => {
    setImageList([]);
    setValue('images', [], { shouldDirty: true });
  };

  const handleUploadError = (err: string) => {
    setMsgAlert({ open: true, message: err, msgType: 'error' });
  };

  // ─── Submit ───────────────────────────────────────────────────────────────────
  const onFormSubmit = (data: TMenuItemFormValues) => {
    const imageFileId = data.images[0] ? imageUrlToFileId(data.images[0]) : '';

    const payload: TCreateMenuItemRequest = {
      name: data.name,
      printName: data.printName || null,
      _idCategory: data._idCategory,
      _idRestaurant: restaurantId,
      description: data.description || '',
      price: { value: data.priceValue, currency: currencyCode },
      takeawayPrice: { value: data.takeawayPriceValue ?? 0, currency: currencyCode },
      images: imageFileId ? [imageFileId] : [],
      enabled: data.enabled,
      printerId: data.printerId ?? null,
      _idAllergens: data._idAllergens,
      attributes: data.attributes.map(({ _idAttribute, value }) => ({ _idAttribute, value })),
      allergies: data.allergies || null,
      nutritions: data.nutritions || null,
      calories: data.calories || null,
      dishnote: data.dishnote || null,
      menuType: data.menuType,
      allowedOrderTypes: data.allowedOrderTypes,
      hideMenuThumbnailImages: data.hideMenuThumbnailImages,
      color: data.color || null,
      textColor: data.textColor || null,
      availabilityRules: data.restrictAvailability
        ? convertSlotsToRules(data.availabilitySlots)
        : [],
      _idModifiers: data._idModifiers,
      options: [],
    };

    const categoryId = data._idCategory;

    if (itemData?._id) {
      updateMutation.mutate(
        { id: itemData._id, payload },
        {
          onSuccess: () => {
            reset(data);
            setMsgAlert({ open: true, message: 'Menu item updated', msgType: 'success' });
          },
          onError: () =>
            setMsgAlert({ open: true, message: 'Failed to update menu item', msgType: 'error' }),
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          reset(data);
          setMsgAlert({ open: true, message: 'Menu item created', msgType: 'success' });
          onSubmitProp(categoryId);
        },
        onError: () =>
          setMsgAlert({ open: true, message: 'Failed to create menu item', msgType: 'error' }),
      });
    }
  };

  // ─── Invalid-submit handler ───────────────────────────────────────────────────
  const SECTION_ERROR_FIELDS: Array<[keyof typeof sectionsExpanded, (keyof TMenuItemFormValues)[]]> = [
    ['basic', ['name', '_idCategory']],
    ['pricing', ['priceValue', 'takeawayPriceValue']],
    ['availability', ['availabilitySlots']],
    ['dietary', ['calories']],
    ['advanced', []],
  ];

  const onInvalidSubmit = (errors: FieldErrors<TMenuItemFormValues>) => {
    console.group('[AddSettingItem] Form validation failed — blocking PUT/POST');
    Object.entries(errors).forEach(([field, error]) => {
      console.error(`  ✗ ${field}:`, error?.message ?? error);
    });
    console.groupEnd();
    let firstSection: keyof typeof sectionsExpanded | null = null;
    setSectionsExpanded((prev) => {
      const next = { ...prev };
      for (const [section, fields] of SECTION_ERROR_FIELDS) {
        if (fields.some((f) => errors[f])) {
          next[section] = true;
          if (!firstSection) firstSection = section;
        }
      }
      return next;
    });
    if (firstSection) {
      const key = firstSection;
      setTimeout(() => scrollToSection(key), 50);
    }
    setMsgAlert({ open: true, message: 'Please fill in all required fields', msgType: 'error' });
  };

  const watchedCategory = watch('_idCategory');
  const categoryName =
    categoryList.find((c: TMenuCategory) => c._id === watchedCategory)?.name ?? '';

  if (isLoading) {
    return <Loader height={{ height: window.innerHeight - 200 }} />;
  }

  return (
    <FormProvider {...formMethods}>
      <Grid
        component="form"
        onSubmit={handleSubmit(onFormSubmit, onInvalidSubmit)}
        container
        direction="column"
        sx={styles.mainGrid}
      >
        <Box sx={styles.editorShell}>
          {/* Hero / summary card */}
          <MenuItemSummaryCard currencyCode={currencyCode} categoryName={categoryName} />

          {/* Section nav */}
          <Box sx={styles.sectionNav}>
            {sectionItems.map((section) => (
              <Box
                key={section.key}
                component="button"
                type="button"
                sx={{ ...styles.sectionChip, backgroundColor: section.bg }}
                onClick={() => scrollToSection(section.key)}
              >
                <Box sx={{ ...styles.sectionChipDot, backgroundColor: section.color }} />
                <Typography sx={styles.sectionChipText}>{section.label}</Typography>
              </Box>
            ))}
          </Box>

          <Grid container spacing={3}>
            {/* Left column: main sections */}
            <Grid item xs={12} md={7}>
              <BasicInfoSection
                accordionRef={basicRef}
                categoryList={categoryList}
                iconColor={getUniqueColor('basic')}
                headerBackground={getUniqueColor('basic', 5)}
                imageList={imageList}
                imageLoading={imageUploading}
                onRemoveImage={handleRemoveImage}
                uploadFile={handleImageUpload}
                uploadError={handleUploadError}
                expanded={sectionsExpanded.basic}
                onToggle={toggleSection('basic')}
              />

              <PricingSection
                accordionRef={pricingRef}
                currencySymbol={currencySymbol}
                iconColor={getUniqueColor('pricing')}
                headerBackground={getUniqueColor('pricing', 5)}
                expanded={sectionsExpanded.pricing}
                onToggle={toggleSection('pricing')}
              />

              <AvailabilitySection
                accordionRef={availabilityRef}
                expanded={sectionsExpanded.availability}
                iconColor={getUniqueColor('availability')}
                headerBackground={getUniqueColor('availability', 5)}
                onToggle={toggleSection('availability')}
              />

              <DietarySection
                accordionRef={dietaryRef}
                allergensList={allergensList}
                attributesList={attributesList}
                expanded={sectionsExpanded.dietary}
                iconColor={getUniqueColor('dietary')}
                headerBackground={getUniqueColor('dietary', 5)}
                onToggle={toggleSection('dietary')}
              />

              <AdvancedSection
                accordionRef={advancedRef}
                printerList={printerList}
                currencyCode={currencyCode}
                expanded={sectionsExpanded.advanced}
                iconColor={getUniqueColor('advanced')}
                headerBackground={getUniqueColor('advanced', 5)}
                onToggle={toggleSection('advanced')}
              />
            </Grid>

            {/* Right column: side panel */}
            <Grid item xs={12} md={5}>
              <Box sx={styles.sidePanel}>
                <TranslationCard
                  itemData={itemData}
                  restaurantId={restaurantId}
                  localizationConfig={restaurantDetail?.localizationConfig}
                />

                <Controller
                  control={formMethods.control}
                  name="_idModifiers"
                  render={({ field }) => (
                    <ModifiersField
                      value={field.value ?? []}
                      onChange={field.onChange}
                      currencyCode={currencyCode}
                      restaurantId={restaurantId}
                      allModifiers={allModifiers}
                      isLoadingModifiers={modifiersLoading}
                      menuItems={menuData as TMenuItemSlim[]}
                      categories={categoryList}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Sticky action bar */}
        <Box sx={styles.actionBar}>
          <Button
            type="button"
            disabled={isSaving}
            sx={styles.cancelBtn}
            onClick={onCancel}
          >
            Cancel
          </Button>
          {itemData && (
            <Button
              type="button"
              disabled={isSaving}
              sx={styles.deleteBtn}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
          )}
          <Button
            disabled={isSaving || imageUploading || (!!itemData && !isDirty)}
            type="submit"
            sx={styles.saveBtn}
          >
            {isSaving ? 'Saving...' : itemData ? 'Update Menu' : 'Add Menu'}
          </Button>
        </Box>

        <AlertMsg
          msgAlert={msgAlert}
          onCloseAlertMsg={() => setMsgAlert({ open: false, message: '', msgType: 'error' })}
        />
      </Grid>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontFamily: 'GilroyBold', fontSize: 16 }}>
          Delete menu item?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: 'GilroyRegular', fontSize: 13 }}>
            This will permanently delete &ldquo;{itemData?.name ?? 'this item'}&rdquo;. This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ textTransform: 'none', fontFamily: 'GilroySemiBold' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', fontFamily: 'GilroySemiBold', borderRadius: '8px' }}
            onClick={() => {
              setDeleteDialogOpen(false);
              setMsgAlert({
                open: true,
                message: 'Delete is not yet wired up — please implement the delete mutation.',
                msgType: 'info',
              });
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default AddSettingItem;

const styles = {
  mainGrid: { width: '100%', minHeight: '100%', py: 1, px: { xs: 2, md: 5 } },
  editorShell: { display: 'flex', flexDirection: 'column', gap: 2.5 },
  sectionNav: { display: 'flex', flexWrap: 'wrap', gap: 1.25, pb: 1.25 },
  sectionChip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    px: 2,
    py: 0.5,
    minHeight: 40,
    borderRadius: '999px',
    backgroundColor: '#FFF',
    border: `1px solid ${THEME_COLOR4}`,
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    outline: 'none',
    font: 'inherit',
    lineHeight: 1,
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      backgroundColor: `${THEME_COLOR}12`,
      borderColor: THEME_COLOR,
    },
  },
  sectionChipDot: { width: 8, height: 8, borderRadius: '50%' },
  sectionChipText: { fontFamily: 'GilroySemiBold', fontSize: 13, color: '#000' },
  sidePanel: { display: 'flex', flexDirection: 'column', gap: 2 },
  actionBar: {
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
    backgroundColor: '#FFF',
    borderTop: `1px solid ${THEME_COLOR4}`,
    mx: { xs: -2, md: -5 },
    px: { xs: 2, md: 5 },
    py: 2,
    mt: 2,
    display: 'flex',
    flexDirection: 'row' as const,
    justifyContent: 'center',
    gap: 2,
  },
  cancelBtn: {
    textTransform: 'capitalize',
    backgroundColor: '#FFF',
    color: '#000',
    fontFamily: 'GilroyBold',
    fontSize: 14,
    borderRadius: 1,
    border: '2px solid #000000',
    width: 150,
    height: 44,
    '&:hover': { backgroundColor: '#FFF !important' },
  },
  deleteBtn: {
    textTransform: 'capitalize',
    backgroundColor: '#FFF',
    color: '#d32f2f',
    fontFamily: 'GilroyBold',
    fontSize: 14,
    borderRadius: 1,
    border: '2px solid #d32f2f',
    width: 150,
    height: 44,
    '&:hover': { backgroundColor: '#FFF !important' },
  },
  saveBtn: {
    backgroundColor: BTN_COLOR,
    textTransform: 'capitalize',
    color: BTN_TEXT_COLOR,
    fontFamily: 'GilroyBold',
    fontSize: 14,
    borderRadius: 1,
    width: 150,
    height: 44,
    opacity: 1,
    transition: 'opacity 0.2s ease',
    '&:hover': { backgroundColor: `${BTN_COLOR} !important`, opacity: 1 },
    '&:active': { opacity: 1 },
    '&.Mui-disabled': { opacity: 0.5, color: BTN_TEXT_COLOR, backgroundColor: BTN_COLOR },
  },
};
